import { NgClass, AsyncPipe } from '@angular/common'
import { Component, CSP_NONCE, DestroyRef, HostListener, inject, LOCALE_ID, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router'
import { Subscription } from 'rxjs'

import { FilterMatchMode, MessageService, ToastMessageOptions, TreeNode } from 'primeng/api'
import { Button } from 'primeng/button'
import { PrimeNG } from 'primeng/config'
import { ConfirmDialog } from 'primeng/confirmdialog'
import { Dialog } from 'primeng/dialog'
import { Drawer } from 'primeng/drawer'
import { DialogService } from 'primeng/dynamicdialog'
import { SelectButton } from 'primeng/selectbutton'
import { StyleClass } from 'primeng/styleclass'
import { Toast } from 'primeng/toast'

import { MsalService } from '@azure/msal-angular'
import { AuthenticationResult } from '@azure/msal-browser'

import { environment } from 'environments/environment'
import { find } from 'lodash-es'

import { ConfirmationService } from '@app/services/confirmation.service'
import { LongRunningJobService } from '@app/services/long-running-job.service'
import { NetworkService } from '@app/services/network.service'
import { TitleService } from '@app/services/title.service'
import { UpdateService } from '@app/services/update.service'
import { UserService } from '@app/services/user.service'
import { Localizable } from '@app/components/generic/generic'
import { clearCache } from '@app/components/generic/utilities'
import { StaffState } from '@app/components/hr/staff/staff.constants'
import { StaffHQState } from '@app/components/hr/staff-hq/staff-hq.constants'
import { StaffImpactState } from '@app/components/hr/staff-impact/staff-impact.constants'
import { ConfirmationModalComponent } from '@app/components/logistics/confirmations/confirmations-workflow-modal'
import { TranslatedLongRunningJob } from '@app/components/long-running-jobs/long-running-job.utils'
import { LongRunningJobsComponent } from '@app/components/long-running-jobs/long-running-jobs-detail'
import { newUser, SessionUser } from '@app/components/roles/user.utils'
import { TourComponent } from '@app/components/tour/tour-detail'
import { ScreeningState } from '@app/components/transparency/screening-entities/screening-entities.constants'
import { UpdateComponent } from '@app/components/updates/updates-detail'
import { stripMsalResponseParams } from '@app/auth/msal-url'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.less',
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService],
  imports: [
    AsyncPipe,
    Button,
    ConfirmDialog,
    Dialog,
    Drawer,
    FormsModule,
    LongRunningJobsComponent,
    NgClass,
    RouterLink,
    RouterOutlet,
    SelectButton,
    StyleClass,
    Toast,
    TourComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly userService = inject(UserService)
  public readonly longRunningJobService = inject(LongRunningJobService)
  private readonly authService = inject(MsalService)
  public readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  public readonly dialogService = inject(DialogService)
  public readonly titleService = inject(TitleService)
  public readonly networkService = inject(NetworkService)
  public readonly updateService = inject(UpdateService)
  private readonly confirmationService = inject(ConfirmationService)
  private readonly messageService = inject(MessageService)
  private readonly primeng = inject(PrimeNG)
  public readonly locale = inject(LOCALE_ID)
  private readonly cspNonce = inject(CSP_NONCE)

  private userSubscription?: Subscription
  private confirmationsSubscription?: Subscription

  public languageOptions: { label: string, value: string }[] = [{ label: 'EN', value: 'en' }, { label: 'FR', value: 'fr' }]
  public sidebarVisible: boolean = true
  public user: SessionUser = newUser()
  public longRunningJobs: TranslatedLongRunningJob[] = []
  public appEnvironment: string = 'dev'
  public currentUrlRoot: string = ''
  public menuNodes: TreeNode[] = []
  public filterLabelField: keyof Localizable = 'label_en'
  public userShouldUpdate: boolean = false
  public gitVersionsVisible: boolean = false

  private readonly RESPONSIVE_MIN_WIDTH = 992
  protected readonly theEnvironment = environment

  @ViewChild(SelectButton) languageSwitcher!: SelectButton

  constructor () {
    // used for drop-down filters
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }
    this.calculateSidebarVisibility(window.innerWidth)
    // listen for url changes and update the `currentUrlRoot` to style the correct menu button
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        const parser = document.createElement('a')
        parser.href = val.url
        // remove the locale from the url to allow the `startsWith()` method to be used for url root matching
        this.currentUrlRoot = parser.pathname.replace(/^\/en\//, '/').replace(/^\/fr\//, '/')
        // when the url changes we send out a message that the page title should probably change.
        this.titleService.sendUpdateTitle()
      }
    })
  }

  ngOnInit () {
    this.primeng.csp.set({ nonce: this.cspNonce! })
    // hide the 'date is not' primeng filter option which isn't useful
    // https://github.com/primefaces/primeng/blob/master/src/app/components/api/primengconfig.ts#L30
    this.primeng.filterMatchModeOptions.date = [
      FilterMatchMode.DATE_IS,
      FilterMatchMode.DATE_BEFORE,
      FilterMatchMode.DATE_AFTER,
    ]
    this.primeng.filterMatchModeOptions.text = [
      FilterMatchMode.CONTAINS,
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS,
      FilterMatchMode.STARTS_WITH,
      FilterMatchMode.ENDS_WITH,
    ]
    this.primeng.filterMatchModeOptions.numeric = [
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS,
      FilterMatchMode.LESS_THAN,
      FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
      FilterMatchMode.GREATER_THAN,
      FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
    ]

    // once-off build the sidebar menu
    this.buildSidebarMenu()

    // subscribe to any changes of the user's currently running LRJs, to reflect them
    // in the spinner widgets
    this.longRunningJobService.getJobs().subscribe(
      jobs => { this.longRunningJobs = jobs },
    )

    this.updateService.getUpdateAvailable().subscribe((updateIsAvailable: boolean) => {
      if (!updateIsAvailable) return
      this.userShouldUpdate = true
      this.dialogService.open(UpdateComponent, {
        header: $localize`:@@topbar-update-dialog-title:Update available`,
        width: '30rem',
        closable: true,
        dismissableMask: true,
        focusOnShow: false,
        modal: true,
      })
    })

    // subscribe to any changes of the currently-logged-in user
    this.userSubscription = this.userService.getUser().subscribe(
      user => { this.user = user },
    )

    // cable up the MSAL redirect (handleRedirectObservable initializes the client itself)
    this.authService.handleRedirectObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: AuthenticationResult | null) => {
        this.clearMsalResponseFromUrl()
        if (result) this.userService.reloadPermissions()
      },
      error: (error) => {
        console.warn('in app component msal error, will call userService.clearUser() and refresh')
        console.warn(error)
        // there was a problem logging in with Azure SSO, fallback to clearing caches and refresh
        // https://stackoverflow.com/questions/66405214/browserautherror-interaction-in-progress-interaction-is-currently-in-progress
        this.userService.clearUser()
        // reload without the auth response params, so the retry does not trip over them again
        window.location.replace(stripMsalResponseParams(window.location.href))
      },
    })

    this.confirmationsSubscription = this.confirmationService.getPendingConfirmations().subscribe(
      confirmation => {
        this.messageService.add({
          sticky: true,
          closable: true,
          severity: 'warn',
          summary: $localize`:@@topbar-pending_confirmation:Pending confirmation ${confirmation.code}:CODE:`,
          data: {
            confirmation: confirmation,
            button: {
              label: $localize`:@@common-view:View`,
              callback: this.displayConfirmation.bind(this),
            },
          },
        })
      },
    )

    // once-off setup config for primeng, mainly for translations
    // https://www.primefaces.org/primeng/i18n
    this.primeng.setTranslation({
      dayNamesMin: [
        $localize`:@@prime-sunday-short:Su`,
        $localize`:@@prime-monday-short:Mo`,
        $localize`:@@prime-tuesday-short:Tu`,
        $localize`:@@prime-wednesday-short:We`,
        $localize`:@@prime-thursday-short:Th`,
        $localize`:@@prime-friday-short:Fr`,
        $localize`:@@prime-saturday-short:Sa`,
      ],
      monthNames: [
        $localize`:@@prime-january:January`,
        $localize`:@@prime-february:February`,
        $localize`:@@prime-march:March`,
        $localize`:@@prime-april:April`,
        $localize`:@@prime-may:May`,
        $localize`:@@prime-june:June`,
        $localize`:@@prime-july:July`,
        $localize`:@@prime-august:August`,
        $localize`:@@prime-september:September`,
        $localize`:@@prime-october:October`,
        $localize`:@@prime-november:November`,
        $localize`:@@prime-december:December`,
      ],
      monthNamesShort: [
        $localize`:@@prime-january-short:Jan`,
        $localize`:@@prime-february-short:Feb`,
        $localize`:@@prime-march-short:Mar`,
        $localize`:@@prime-april-short:Apr`,
        $localize`:@@prime-may-short:May`,
        $localize`:@@prime-june-short:Jun`,
        $localize`:@@prime-july-short:Jul`,
        $localize`:@@prime-august-short:Aug`,
        $localize`:@@prime-september-short:Sep`,
        $localize`:@@prime-october-short:Oct`,
        $localize`:@@prime-november-short:Nov`,
        $localize`:@@prime-december-short:Dec`,
      ],
      startsWith: $localize`:@@prime-startsWith:Starts with`,
      contains: $localize`:@@prime-contains:Contains`,
      notContains: $localize`:@@prime-notContains:Not contains`,
      endsWith: $localize`:@@prime-endsWith:Ends with`,
      equals: $localize`:@@prime-equals:Equals`,
      notEquals: $localize`:@@prime-notEquals:Not equals`,
      noFilter: $localize`:@@prime-noFilter:No Filter`,
      lt: $localize`:@@prime-lt:Less than`,
      lte: $localize`:@@prime-lte:Less than or equal to`,
      gt: $localize`:@@prime-gt:Greater than`,
      gte: $localize`:@@prime-gte:Greater than or equal to`,
      is: $localize`:@@prime-is:Is`,
      isNot: $localize`:@@prime-isNot:Is not`,
      before: $localize`:@@prime-before:Before`,
      after: $localize`:@@prime-after:After`,
      dateIs: $localize`:@@prime-dateIs:Date is`,
      dateIsNot: $localize`:@@prime-dateIsNot:Date is not`,
      dateBefore: $localize`:@@prime-dateBefore:Date is before`,
      dateAfter: $localize`:@@prime-dateAfter:Date is after`,
      clear: $localize`:@@prime-clear:Clear`,
      apply: $localize`:@@prime-apply:Apply`,
      matchAll: $localize`:@@prime-matchAll:Match all`,
      matchAny: $localize`:@@prime-matchAny:Match any`,
      addRule: $localize`:@@prime-addRule:Add rule`,
      removeRule: $localize`:@@prime-removeRule:Remove rule`,
      accept: $localize`:@@prime-accept:Yes`,
      reject: $localize`:@@prime-reject:No`,
      dateFormat: 'yy-mm-dd',
      firstDayOfWeek: 1,
      today: $localize`:@@prime-today:Today`,
      emptyMessage: $localize`:@@prime-emptyMessage:No results found`,
      emptyFilterMessage: $localize`:@@prime-emptyFilterMessage:No results found`,
    })

    // used for the watermark
    if (environment.production) {
      this.appEnvironment = 'production'
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize (event: any) {
    this.calculateSidebarVisibility(event.target.innerWidth)
  }

  private calculateSidebarVisibility (innerWidth: number) {
    this.sidebarVisible = innerWidth > this.RESPONSIVE_MIN_WIDTH
  }

  /**
   * Define the menu structure according to PrimeNG TreeNodes
   */
  buildSidebarMenu () {
    const countryLabelField = 'country__' + this.filterLabelField

    this.menuNodes = [{
      label: $localize`:@@sidebar-home:Home`,
      data: {
        initialRouteGroup: 'home',
        currentUrlRoot: '/',
        hasPermissionIn: [],
        routerLink: ['/'],
        negativeStyle: true,
        iconClass: 'fa-solid fa-house',
        id: 'homeMenu',
      },
    }, {
      label: $localize`:@@sidebar-permissions-center:Permissions Center`,
      data: {
        initialRouteGroup: 'roles',
        hasPermissionIn: ['view_role'],
        negativeStyle: true,
        iconClass: 'fa-solid fa-user-gear',
      },
      children: [{
        label: $localize`:@@sidebar-roles:Roles`,
        data: {
          currentUrlRoot: '/roles/',
          routerLink: ['roles', 'list'],
          hasPermissionIn: ['view_role'],
        },
      }, {
        label: $localize`:@@sidebar-logistics-asset_user_accesses:Assets access`,
        data: {
          currentUrlRoot: '/logistics/asset-user-accesses/',
          routerLink: ['logistics', 'asset-user-accesses', 'list'],
          hasPermissionIn: ['view_assetuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-greening-caroot-user-accesses:CAROOT access`,
        data: {
          currentUrlRoot: '/greening/caroot-user-accesses/',
          routerLink: ['greening', 'caroot-user-accesses', 'list'],
          hasPermissionIn: ['view_carootuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-crm-access:COMPFU access`,
        data: {
          currentUrlRoot: '/crm-user-accesses/',
          routerLink: ['crm-user-accesses', 'list'],
          hasPermissionIn: ['view_crmuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-external-audit-user-accesses:External audits access`,
        data: {
          currentUrlRoot: '/transparency/external-audit-user-accesses/',
          routerLink: ['transparency', 'external-audit-user-accesses', 'list'],
          hasPermissionIn: ['view_externalaudituseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-financial_sheet_user_accesses:Financial sheets access`,
        data: {
          currentUrlRoot: '/finance/financial-sheet-user-accesses/',
          routerLink: ['finance', 'financial-sheet-user-accesses', 'list'],
          hasPermissionIn: ['view_financialsheetuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-grasp-accesses:GRASP access`,
        data: {
          currentUrlRoot: '/grasp/grasp-accesses',
          routerLink: ['grasp', 'grasp-accesses', 'list'],
          hasPermissionIn: ['view_graspaccess'],
        },
      }, {
        label: $localize`:@@sidebar-incident-user-access:Incidents access`,
        data: {
          currentUrlRoot: '/security/incident-user-accesses/',
          routerLink: ['security', 'incident-user-accesses', 'list'],
          hasPermissionIn: ['view_incidentuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-partners_user_access:Partners access`,
        data: {
          currentUrlRoot: '/grants-management/partner-user-accesses/',
          routerLink: ['grants-management', 'partner-user-accesses', 'list'],
          hasPermissionIn: ['add_partneruseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-premises-user-access:Premises access`,
        data: {
          currentUrlRoot: '/security/premises-user-accesses/',
          routerLink: ['security', 'premises-user-accesses', 'list'],
          hasPermissionIn: ['view_premisesuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-premises-lifecycle-user-accesses:Premises lifecycle accesses`,
        data: {
          currentUrlRoot: '/security/premises-lifecycle-user-accesses/',
          routerLink: ['security', 'premises-lifecycle-user-accesses', 'list'],
          hasPermissionIn: ['view_premiseslifecycleuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-procurement_user_accesses:Procurements access`,
        data: {
          currentUrlRoot: '/logistics/procurement-user-accesses/',
          routerLink: ['logistics', 'procurement-user-accesses', 'list'],
          hasPermissionIn: ['view_procurementuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-screening-request-user-accesses:Screening access`,
        data: {
          currentUrlRoot: '/transparency/screening-request-user-accesses/',
          routerLink: ['transparency', 'screening-request-user-accesses', 'list'],
          hasPermissionIn: ['view_screeningrequestuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-staff-national-user-access:Staff (national) access`,
        data: {
          currentUrlRoot: '/hr/staff-national-user-accesses/',
          routerLink: ['hr', 'staff-national-user-accesses', 'list'],
          hasPermissionIn: ['view_staffnationaluseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-staff_impact_user_accesses:Staff IMPACT access`,
        data: {
          currentUrlRoot: '/hr/staff-impact-user-accesses/',
          routerLink: ['hr', 'staff-impact-user-accesses', 'list'],
          hasPermissionIn: ['view_staffimpactuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-staff_recommendation_user_accesses:Staff recommendation access`,
        data: {
          currentUrlRoot: '/hr/staff-recommendation-user-accesses/',
          routerLink: ['hr', 'staff-recommendation-user-accesses', 'list'],
          hasPermissionIn: ['view_staffrecommendationuseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-supplier-user-access:Suppliers access`,
        data: {
          currentUrlRoot: '/logistics/supplier-user-accesses/',
          routerLink: ['logistics', 'supplier-user-accesses', 'list'],
          hasPermissionIn: ['view_supplieruseraccess'],
        },
      }, {
        label: $localize`:@@sidebar-programme-water_structure_user_access:Water structures access`,
        data: {
          currentUrlRoot: '/programme/water-structure-user-accesses/',
          routerLink: ['programme', 'water-structure-user-accesses', 'list'],
          hasPermissionIn: ['view_waterstructureuseraccess'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-locations:Locations`,
      data: {
        initialRouteGroup: 'admin-units',
        hasPermissionIn: ['view_country'],
        negativeStyle: true,
        iconClass: 'fa-solid fa-location-dot',
      },
      children: [{
        label: $localize`:@@sidebar-countries:Countries`,
        data: {
          currentUrlRoot: '/countries/',
          routerLink: ['countries', 'list'],
          queryParams: { is_acted: true },
          hasPermissionIn: ['view_country'],
        },
      }, {
        label: $localize`:@@sidebar-admin-zones-1:Admin zones 1`,
        data: {
          currentUrlRoot: '/admin-zones-1/',
          routerLink: ['admin-zones-1', 'list'],
          hasPermissionIn: ['view_adminzone1'],
        },
      }, {
        label: $localize`:@@sidebar-admin-zones-2:Admin zones 2`,
        data: {
          currentUrlRoot: '/admin-zones-2/',
          routerLink: ['admin-zones-2', 'list'],
          hasPermissionIn: ['view_adminzone2'],
        },
      }, {
        label: $localize`:@@sidebar-acted-areas:Acted areas`,
        data: {
          currentUrlRoot: '/acted-areas/',
          routerLink: ['acted-areas', 'list'],
          // TODO: temporary hack to hide the areas menu for users unable to write areas, until the data
          // is ready for public consumption
          hasPermissionIn: ['change_actedarea'],
        },
      }, {
        label: $localize`:@@sidebar-places:Places`,
        data: {
          currentUrlRoot: '/places/',
          routerLink: ['places', 'list'],
          queryParams: { code__isnotnull: true },
          hasPermissionIn: ['view_place'],
        },
      }, {
        label: $localize`:@@sidebar-map:Map`,
        data: {
          currentUrlRoot: '/map',
          routerLink: ['map'],
          hasPermissionIn: ['view_place'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-projects-management:Projects management`,
      data: {
        initialRouteGroup: 'projects',
        hasPermissionIn: ['view_project', 'view_project_reporting'],
        iconClass: 'fa-solid fa-chart-column',
      },
      children: [{
        label: $localize`:@@sidebar-donors:Donors`,
        data: {
          currentUrlRoot: '/donors/',
          routerLink: ['donors', 'list'],
          hasPermissionIn: ['view_donor'],
        },
      }, {
        label: $localize`:@@sidebar-partners:Partners`,
        data: {
          currentUrlRoot: '/grants-management/partners/',
          routerLink: ['grants-management', 'partners', 'list'],
          hasPermissionIn: ['view_partner'],
        },
      }, {
        label: $localize`:@@sidebar-projects:Projects`,
        data: {
          currentUrlRoot: '/grants-management/projects/',
          routerLink: ['grants-management', 'projects', 'list'],
          hasPermissionIn: ['view_project'],
        },
      }, {
        label: $localize`:@@sidebar-project-reportings:Project reporting`,
        data: {
          currentUrlRoot: '/project-report-schedules/',
          routerLink: ['project-report-schedules', 'list'],
          hasPermissionIn: ['view_project_reporting'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-ame:MEAL`,
      data: {
        initialRouteGroup: 'ame',
        hasPermissionIn: ['view_complaint', 'view_deliverables'],
        iconClass: 'fa-solid fa-file-lines',
      },
      children: [{
        label: $localize`:@@sidebar-complaints:COMPFU`,
        data: {
          currentUrlRoot: '/complaints/',
          routerLink: ['complaints', 'list'],
          hasPermissionIn: ['view_complaint'],
          id: 'crmMenu', // used by the tour to scroll to target
        },
      }, {
        label: $localize`:@@sidebar-ame-sub-places:MEAL sub-places`,
        data: {
          currentUrlRoot: '/meal-sub-places/',
          routerLink: ['meal-sub-places', 'list'],
          hasPermissionIn: ['view_amesubplace'],
        },
      }, {
        label: $localize`:@@sidebar-deliverables:Deliverables`,
        data: {
          currentUrlRoot: '/deliverables/',
          routerLink: ['deliverables', 'list'],
          hasPermissionIn: ['view_deliverables'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-finance:Finance`,
      data: {
        initialRouteGroup: 'finance',
        hasPermissionIn: ['view_currencyrate', 'view_balzacbook', 'view_financialsheet', 'change_project_financial_closure', 'view_finance_hq'],
        iconClass: 'fa-solid fa-money-bills',
      },
      children: [{
        label: $localize`:@@sidebar-balzac-books:BALZAC books`,
        data: {
          currentUrlRoot: '/balzac-books/',
          routerLink: ['balzac-books', 'list'],
          queryParams: { status: 'active' },
          hasPermissionIn: ['view_balzacbook'],
        },
      }, {
        label: $localize`:@@sidebar-contracts_closures:Project closure info`,
        data: {
          currentUrlRoot: '/finance/contracts-closures/',
          routerLink: ['finance', 'contracts-closures', 'list'],
          queryParams: { financially_closed: false },
          hasPermissionIn: ['change_project_financial_closure'],
        },
      }, {
        label: $localize`:@@sidebar-exchange-rates:Exchange rates`,
        data: {
          currentUrlRoot: '/finance/currency-rates/',
          routerLink: ['finance', 'currency-rates', 'list'],
          hasPermissionIn: ['view_currencyrate'],
        },
      }, {
        label: $localize`:@@sidebar-financial_sheets:Financial Sheets`,
        data: {
          currentUrlRoot: '/finance/financial-sheets/',
          routerLink: ['finance', 'financial-sheets', 'list'],
          hasPermissionIn: ['view_financialsheet'],
        },
      }, {
        label: $localize`:@@sidebar-saga_referential:SAGA referential`,
        data: {
          currentUrlRoot: '/finance/saga/saga-referential',
          routerLink: ['finance', 'saga', 'saga-referential'],
          hasPermissionIn: ['view_financialsheet'],
        },
      }, {
        label: $localize`:@@sidebar-saga-conversions:SAGA conversions`,
        data: {
          currentUrlRoot: '/finance/saga/saga-conversions',
          routerLink: ['finance', 'saga', 'saga-conversions'],
          hasPermissionIn: ['change_balzacbook'],
        },
      }, {
        label: $localize`:@@sidebar-finance-operations:Finance & Operations`,
        data: {
          hasPermissionIn: ['view_finance_hq'],
          externalLink: environment.financeOperationsUrl,
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }, {
        label: $localize`:@@sidebar-finance-powerapps:Finance PowerApps`,
        data: {
          hasPermissionIn: ['view_finance_hq'],
          externalLink: environment.financePowerAppsUrl,
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }],
    }, {
      label: $localize`:@@sidebar-logistics:Logistics`,
      data: {
        initialRouteGroup: 'logistics',
        hasPermissionIn: [
          'view_asset', 'view_asset_all', 'view_disposal_plan', 'view_disposal_plan_all', 'view_inventory', 'view_inventory_all',
          'view_supplier', 'view_procurementplanversion', 'view_waiver', 'view_phoneline',
        ],
        iconClass: 'fa-solid fa-truck',
      },
      children: [{
        label: $localize`:@@sidebar-logistics-assets:Assets`,
        data: {
          initialRouteGroup: 'assets',
          hasPermissionIn: ['view_asset', 'view_asset_all', 'view_disposal_plan', 'view_disposal_plan_all', 'view_inventory', 'view_inventory_all'],
        },
        children: [{
          label: $localize`:@@sidebar-logistics-assets:Assets`,
          data: {
            currentUrlRoot: '/logistics/assets/',
            routerLink: ['logistics', 'assets', 'list'],
            hasPermissionIn: ['view_asset', 'view_asset_all'],
          },
        }, {
          label: $localize`:@@sidebar-logistics-disposal_plans:Inventory removal plans`,
          data: {
            currentUrlRoot: '/logistics/disposal-plans/',
            routerLink: ['logistics', 'disposal-plans', 'list'],
            hasPermissionIn: ['view_disposalplan', 'view_disposalplan_all'],
          },
        }, {
          label: $localize`:@@sidebar-logistics-inventories:Inventories`,
          data: {
            currentUrlRoot: '/logistics/inventories/',
            routerLink: ['logistics', 'inventories', 'list'],
            hasPermissionIn: ['view_inventory', 'view_inventory_all'],
          },
        }],
      }, {
        label: $localize`:@@sidebar-logistics-procurements:Procurements`,
        data: {
          initialRouteGroup: 'procurements',
          hasPermissionIn: ['view_supplier', 'view_procurementplanversion', 'view_waiver'],
        },
        children: [{
          label: $localize`:@@sidebar-logistics-projects:Procurement plans`,
          data: {
            currentUrlRoot: '/logistics/projects/',
            routerLink: ['logistics', 'projects', 'list'],
            hasPermissionIn: ['view_procurementplanversion'],
          },
        }, {
          label: $localize`:@@sidebar-suppliers:Suppliers`,
          data: {
            currentUrlRoot: '/logistics/suppliers/',
            routerLink: ['logistics', 'suppliers', 'list'],
            hasPermissionIn: ['view_supplier'],
            id: 'suppliersMenu', // used by the tour to scroll to target
          },
        }, {
          label: $localize`:@@sidebar-waivers:Waivers`,
          data: {
            currentUrlRoot: '/logistics/waivers/',
            routerLink: ['logistics', 'waivers', 'list'],
            hasPermissionIn: ['view_waiver'],
          },
        }],
      }, {
        label: $localize`:@@sidebar-logistics-phone_lines:Phone lines`,
        data: {
          currentUrlRoot: '/logistics/phone-lines/',
          routerLink: ['logistics', 'phone-lines', 'list'],
          hasPermissionIn: ['view_phoneline'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-hr:HR`,
      data: {
        initialRouteGroup: 'hr',
        hasPermissionIn: [
          'view_staffrecommendation',
          'view_staffhq',
          'view_staffimpact',
          'view_staffnational',
          'view_worlddirectory',
          'view_visarestrictionassignment',
          'view_directflight',
          'view_focalpoint',
          'view_hq_leaves',
          'view_nat_leaves',
        ],
        iconClass: 'fa-solid fa-users',
      },
      children: [{
        label: $localize`:@@sidebar-hr-administration:Administration HR`,
        data: {
          initialRouteGroup: 'hr-admin',
          hasPermissionIn: [
            'view_staffnational',
            'view_staffnationalchange',
            'view_staffhq',
            'view_visarestrictionassignment',
            'view_directflight',
            'view_staffimpact',
          ],
        },
        children: [{
          label: $localize`:@@sidebar-staff-national:National staff`,
          data: {
            currentUrlRoot: '/hr/staff-national/',
            routerLink: ['hr', 'staff-national', 'list'],
            queryParams: { state__in: ['active,error'] },
            hasPermissionIn: ['view_staffnational'],
          },
        }, {
          label: $localize`:@@sidebar-staff-national-changes:National staff changes`,
          data: {
            currentUrlRoot: '/hr/staff-national-changes/',
            routerLink: ['hr', 'staff-national-changes', 'list'],
            hasPermissionIn: ['view_staffnationalchange'],
          },
        }, {
          label: $localize`:@@sidebar-staff-hq:Staff HQ/Expats`,
          data: {
            currentUrlRoot: '/hr/staff-hq/',
            routerLink: ['hr', 'staff-hq', 'list'],
            queryParams: {
              state__in: [
                StaffHQState.CREATED,
                StaffHQState.INITIALISED,
                StaffHQState.CREATED_AZURE,
                StaffHQState.ACTIVE,
                StaffHQState.ERROR_INITIALISING,
                StaffHQState.ERROR_AZURE,
                StaffHQState.ERROR_TALENTIA,
              ].join(','),
              ordering: '-created_at',
            },
            hasPermissionIn: ['view_staffhq'],
          },
        }, {
          label: $localize`:@@sidebar-staff_impact:Staff Impact`,
          data: {
            currentUrlRoot: '/hr/staff-impact/',
            routerLink: ['hr', 'staff-impact', 'list'],
            queryParams: { state__in: [StaffImpactState.ACTIVE, StaffImpactState.ERROR_AZURE].join(',') },
            hasPermissionIn: ['view_staffimpact'],
          },
        }, {
          label: $localize`:@@sidebar-visa-applications:Visa restrictions`,
          data: {
            initialRouteGroup: 'hr-visa',
            hasPermissionIn: ['view_visarestrictionassignment', 'view_directflight'],
          },
          children: [{
            label: $localize`:@@sidebar-visa-restriction-assignments:Countries`,
            data: {
              currentUrlRoot: '/hr/visa-restriction-assignments/',
              routerLink: ['hr', 'visa-restriction-assignments', 'list'],
              hasPermissionIn: ['view_visarestrictionassignment'],
            },
          }, {
            label: $localize`:@@sidebar-direct-flights:Evacuation`,
            data: {
              currentUrlRoot: '/hr/direct-flights/',
              routerLink: ['hr', 'direct-flights', 'list'],
              hasPermissionIn: ['view_directflight'],
            },
          }, {
            label: $localize`:@@sidebar-visa-restriction-search:Search`,
            data: {
              currentUrlRoot: '/hr/visa-search/',
              routerLink: ['hr', 'visa-search'],
              hasPermissionIn: ['view_visarestrictionassignment'],
            },
          }],
        }],
      }, {
        label: $localize`:@@sidebar-world-directory:World directory`,
        data: {
          currentUrlRoot: '/hr/world-directory/',
          routerLink: ['hr', 'world-directory', 'list'],
          queryParams: { ordering: countryLabelField + ',surname', state__in: StaffState.ACTIVE },
          hasPermissionIn: ['view_worlddirectory'],
        },
      }, {
        label: $localize`:@@sidebar-focal_points:Focal points`,
        data: {
          currentUrlRoot: '/hr/focal-points/',
          routerLink: ['hr', 'focal-points', 'list'],
          hasPermissionIn: ['view_focalpoint'],
        },
      }, {
        label: $localize`:@@sidebar-staff_recommendation:Staff recommendation`,
        data: {
          currentUrlRoot: '/hr/staff-recommendation/',
          routerLink: ['hr', 'staff-recommendation', 'list'],
          hasPermissionIn: ['view_staffrecommendation'],
        },
      }, {
        label: $localize`:@@sidebar-myleaves-national:MyLeaves - National`,
        data: {
          currentUrlRoot: '/hr/my-leaves',
          routerLink: ['hr', 'my-leaves'],
          hasPermissionIn: ['view_nat_leaves'],
        },
      }, {
        label: $localize`:@@sidebar-sohorsys-hq-leaves:SoHorsys - HQ Leaves request`,
        data: {
          hasPermissionIn: ['view_hq_leaves'],
          externalLink: 'https://acted.sohorsys.fr/SoHorsys',
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }],
    }, {
      label: $localize`:@@sidebar-transparency:Audit & transparency`,
      data: {
        initialRouteGroup: 'transparency',
        hasPermissionIn: ['view_screeningrequest', 'view_screeningentity', 'view_externalaudit', 'view_pseah', 'view_transparencyline'],
        iconClass: 'fa-solid fa-magnifying-glass',
      },
      children: [{
        label: $localize`:@@sidebar-external-audits:External audits`,
        data: {
          currentUrlRoot: '/transparency/external-audits/',
          routerLink: ['transparency', 'external-audits', 'list'],
          hasPermissionIn: ['view_externalaudit'],
        },
      }, {
        label: $localize`:@@sidebar-external-audits-firms-and-applicants:Firms and applicants`,
        data: {
          currentUrlRoot: '/transparency/external-audit-firms-and-applicants',
          routerLink: ['transparency', 'external-audit-firms-and-applicants'],
          hasPermissionIn: ['view_externalaudit'],
        },
      }, {
        label: $localize`:@@sidebar-pseah-checklist:PSEAH Checklist`,
        data: {
          currentUrlRoot: '/transparency/pseah-checklist',
          routerLink: ['transparency', 'pseah-checklist'],
          hasPermissionIn: ['view_pseah'],
        },
      }, {
        label: $localize`:@@sidebar-screening-requests:Screening requests`,
        data: {
          currentUrlRoot: '/transparency/screening-requests/',
          routerLink: ['transparency', 'screening-requests', 'list'],
          queryParams: this.filterScreening(),
          hasPermissionIn: ['view_screeningrequest'],
        },
      }, {
        label: $localize`:@@sidebar-screening-entities:Screening entities`,
        data: {
          currentUrlRoot: '/transparency/screening-entities/',
          routerLink: ['transparency', 'screening-entities', 'list'],
          queryParams: this.filterScreening(),
          hasPermissionIn: ['view_screeningentity'],
        },
      }, {
        label: $localize`:@@sidebar-certifications:Donor eligibilities`,
        data: {
          currentUrlRoot: '/transparency/certifications/',
          routerLink: ['transparency', 'certifications', 'list'],
          hasPermissionIn: ['view_certification_all'],
        },
      }, {
        label: $localize`:@@sidebar-transparency-line:Transparency Line`,
        data: {
          currentUrlRoot: '/transparency/transparency-line',
          routerLink: ['transparency', 'transparency-line'],
          hasPermissionIn: ['view_transparencyline'],
        },
      }],
    }, {
      label: $localize`:@@sidebar-security:Security`,
      data: {
        initialRouteGroup: 'security',
        hasPermissionIn: ['view_incident', 'add_incident', 'view_premises', 'view_sec21form', 'view_premisesclosurerequest'],
        iconClass: 'fa-solid fa-shield-halved',
      },
      children: [{
        label: $localize`:@@sidebar-incidents:Incidents`,
        data: {
          currentUrlRoot: '/security/incidents/',
          routerLink: ['security', 'incidents', 'list'],
          hasPermissionIn: ['view_incident', 'add_incident'],
        },
      }, {
        label: $localize`:@@sidebar-premises:Premises`,
        data: {
          currentUrlRoot: '/security/premises/',
          routerLink: ['security', 'premises', 'list'],
          queryParams: { status: 'active' },
          hasPermissionIn: ['view_premises'],
        },
      }, {
        label: $localize`:@@sidebar-sec21-forms:New Premises Requests (SEC-21)`,
        data: {
          currentUrlRoot: '/security/sec21-forms/',
          routerLink: ['security', 'sec21-forms', 'list'],
          hasPermissionIn: ['view_sec21form'],
        },
      }, {
        label: $localize`:@@sidebar-premises_closure_requests:Premises Exit Requests (SEC-22)`,
        data: {
          currentUrlRoot: '/security/premises-closure-requests/',
          routerLink: ['security', 'premises-closure-requests', 'list'],
          hasPermissionIn: ['view_premisesclosurerequest'],
        },
      }, {
        label: $localize`:@@sidebar-sec19:Movements (SEC-19)`,
        data: {
          currentUrlRoot: '/security/sec19',
          routerLink: ['security', 'sec19'],
          hasPermissionIn: [],
        },
      }],
    }, {
      label: $localize`:@@sidebar-information-system:Information Systems`,
      data: {
        initialRouteGroup: 'information-system',
        hasPermissionIn: [],
        iconClass: 'fa-solid fa-file-lines',
      },
      children: [{
        label: $localize`:@@sidebar-helpdesk:Helpdesk`,
        data: {
          hasPermissionIn: [],
          externalLink: 'https://tasks.acted.org/servicedesk/customer/portal/1',
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }, {
        label: $localize`:@@sidebar-digital-catalog:Digital Catalog`,
        data: {
          hasPermissionIn: [],
          externalLink: 'https://acted.sharepoint.com/sites/ActedGlobal/SitePages/Modern-IT-Service-Catalog--Digital-Solutions-for-Productivity,-Security,-and-Collaboration.aspx',
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }, {
        label: $localize`:@@sidebar-tech-tips:Tech Tips`,
        data: {
          hasPermissionIn: [],
          externalLink: 'https://acted.sharepoint.com/sites/ActedGlobal',
          isExternal: true,
          iconClass: 'fa-solid fa-external-link',
        },
      }],
    }, {
      label: $localize`:@@sidebar-greening-acted:Greening Acted`,
      data: {
        initialRouteGroup: 'greening',
        hasPermissionIn: ['view_graspentry', 'view_carootreport'],
        iconClass: 'fa-solid fa-tree',
      },
      children: [{
        label: $localize`:@@sidebar-grasp:GRASP`,
        data: {
          currentUrlRoot: '/grasp/grasp-entries/',
          routerLink: ['grasp', 'grasp-entries', 'list'],
          hasPermissionIn: ['view_graspentry'],
        },
      }, {
        label: $localize`:@@sidebar-greening-caroot:CAROOT`,
        data: {
          initialRouteGroup: 'caroot',
          hasPermissionIn: ['view_carootreport'],
        },
        children: [{
          label: $localize`:@@sidebar-greening-caroot-perimeters:Perimeters`,
          data: {
            currentUrlRoot: '/greening/caroot-perimeters/',
            routerLink: ['greening/caroot-perimeters', 'list'],
            hasPermissionIn: ['view_carootperimeter'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-emission-factors:Emission factors`,
          data: {
            currentUrlRoot: '/greening/caroot-emission-factors/',
            routerLink: ['greening/caroot-emission-factors', 'list'],
            hasPermissionIn: ['view_carootemissionfactor'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-activities:Activities`,
          data: {
            currentUrlRoot: '/greening/caroot-activities/',
            routerLink: ['greening/caroot-activities', 'list'],
            hasPermissionIn: ['view_carootactivity'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-report-country-configurations:Country configurations`,
          data: {
            currentUrlRoot: '/greening/caroot-report-country-configurations/',
            routerLink: ['greening', 'caroot-report-country-configurations', 'list'],
            hasPermissionIn: ['view_carootreportcountryconfiguration'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-report-place-configurations:Place configurations`,
          data: {
            currentUrlRoot: '/greening/caroot-report-place-configurations/',
            routerLink: ['greening/caroot-report-place-configurations', 'list'],
            hasPermissionIn: ['view_carootreportplaceconfiguration'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-reports:Reports`,
          data: {
            currentUrlRoot: '/greening/caroot-reports/',
            routerLink: ['greening', 'caroot-reports', 'list'],
            hasPermissionIn: ['view_carootreport'],
          },
        }, {
          label: $localize`:@@sidebar-greening-caroot-country-reports:Country reports`,
          data: {
            currentUrlRoot: '/greening/caroot-country-reports/',
            routerLink: ['greening', 'caroot-country-reports', 'list'],
            hasPermissionIn: ['view_carootcountryreport'],
          },
        }],
      }],
    }, {
      label: $localize`:@@sidebar-archives:Archives`,
      data: {
        initialRouteGroup: 'archives',
        hasPermissionIn: [],
        iconClass: 'fa-solid fa-cloud-arrow-up',
        externalLink: environment.archivesUrl,
        isExternal: true,
        id: 'archivesMenu',
      },
    }, {
      label: $localize`:@@sidebar-programme:Programme`,
      data: {
        initialRouteGroup: 'programme',
        hasPermissionIn: ['view_waterstructure'],
        iconClass: 'fa-solid fa-shapes',
      },
      children: [{
        label: $localize`:@@sidebar-programme-water_structures:Water structures`,
        data: {
          currentUrlRoot: '/programme/water-structures/',
          routerLink: ['programme', 'water-structures', 'list'],
          hasPermissionIn: ['view_waterstructure'],
        },
      }, {
        label: $localize`:@@sidebar-programme-water_networks:Water networks`,
        data: {
          currentUrlRoot: '/programme/water-networks/',
          routerLink: ['programme', 'water-networks', 'list'],
          hasPermissionIn: ['view_waternetwork'],
        },
      }],
    }]
  }

  openExternalLink (url: string) {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  private filterScreening () {
    if (this.userService.hasPermissionIn(['view_screeningrequest_all'])) {
      return { state__in: Object.values(ScreeningState).filter(s => s !== ScreeningState.DRAFT).join(',') }
    } else {
      return {}
    }
  }

  /**
   * Return whether the given 'named group' matches the current route: used for pre-opening a menu group if we are deep-linked to it
   */
  routeIsInGroup (groupName: string): boolean {
    let possibleRoots: string[] = []

    if (groupName === 'admin-units') {
      possibleRoots = ['/countries/', '/admin-zones-1/', '/admin-zones-2/', '/acted-areas/', '/places/', '/map']
    } else if (groupName === 'ame') {
      possibleRoots = ['/complaints/', '/meal-sub-places/', '/deliverables/']
    } else if (groupName === 'caroot') {
      possibleRoots = ['/greening/']
    } else if (groupName === 'finance') {
      possibleRoots = [
        '/balzac-books/',
        '/finance/',
      ]
    } else if (groupName === 'greening') {
      possibleRoots = ['/grasp/grasp-entries/', '/greening/']
    } else if (groupName === 'hr') {
      possibleRoots = [
        '/hr/staff-national/',
        '/hr/staff-national-changes/',
        '/hr/staff-hq/',
        '/hr/staff-impact/',

        '/hr/visa-restriction-assignments/',
        '/hr/direct-flights/',
        '/hr/visa-search',

        '/hr/staff-recommendation/',
        '/hr/world-directory/',
        '/hr/focal-points/',
        '/hr/my-leaves',
      ]
    } else if (groupName === 'hr-admin') {
      possibleRoots = [
        '/hr/staff-national/',
        '/hr/staff-national-changes/',
        '/hr/staff-hq/',
        '/hr/staff-impact/',

        '/hr/visa-restriction-assignments/',
        '/hr/direct-flights/',
        '/hr/visa-search',
      ]
    } else if (groupName === 'hr-visa') {
      possibleRoots = [
        '/hr/visa-restriction-assignments/',
        '/hr/direct-flights/',
        '/hr/visa-search',
      ]
    } else if (groupName === 'logistics') {
      possibleRoots = [
        '/logistics/',
        '/logistics/phone-lines',
      ]
    } else if (groupName === 'assets') {
      possibleRoots = ['/logistics/assets/', '/logistics/disposal-plans/', '/logistics/inventories/']
    } else if (groupName === 'procurements') {
      possibleRoots = ['/logistics/projects/', '/logistics/suppliers/', '/logistics/waivers/']
    } else if (groupName === 'programme') {
      possibleRoots = ['/programme/water-structures/', '/programme/water-networks/']
    } else if (groupName === 'projects') {
      possibleRoots = ['/grants-management/projects/', '/donors/', '/grants-management/partners/', '/project-report-schedules/']
    } else if (groupName === 'roles') {
      possibleRoots = [
        '/roles/', '/crm-user-accesses/', '/grasp/grasp-accesses/', '/logistics/supplier-user-accesses/',
        '/logistics/procurement-user-accesses/', '/security/incident-user-accesses/', '/security/premises-user-accesses/',
        '/greening/caroot-user-accesses/', '/transparency/screening-request-user-accesses/', '/finance/financial-sheet-user-accesses/',
        '/transparency/external-audit-user-accesses/', '/security/premises-lifecycle-user-accesses/', '/hr/staff-national-user-accesses/',
        '/grants-management/partner-user-accesses/', '/programme/water-structure-user-accesses/', '/logistics/asset-user-accesses/',
        '/hr/staff-recommendation-user-accesses/',
      ]
    } else if (groupName === 'security') {
      possibleRoots = ['/security/incidents/', '/security/premises/', '/security/sec21-forms/', '/security/premises-closure-requests/', '/security/sec19']
    } else if (groupName === 'transparency') {
      possibleRoots = [
        '/transparency/certifications/',
        '/transparency/external-audits/',
        '/transparency/external-audit-firms-and-applicants',
        '/transparency/pseah-checklist',
        '/transparency/screening-requests/',
        '/transparency/screening-entities/',
        '/transparency/transparency-line',
      ]
    }

    return !!find(possibleRoots, (root: string) => {
      return this.currentUrlRoot.startsWith(root)
    })
  }

  getUserInitials () {
    if (this.user.anonymous) return ''
    const initials = (this.user.first_name ?? '').substring(0, 1) + (this.user.last_name ?? '').substring(0, 1)
    return initials.toUpperCase()
  }

  onChangeLang ($event: { originalEvent?: Event, value?: string }) {
    if (!$event?.value) return
    const newLocale = $event.value.toLocaleLowerCase()
    const baseUrl = window.location.origin
    const newLocation = window.location.href.replace(`${baseUrl}/${this.locale}/`, `${baseUrl}/${newLocale}/`)
    if (newLocation.startsWith(`${baseUrl}/`)) {
      window.location.href = newLocation
      this.languageSwitcher.value = this.languageOptions.filter(o => o.value !== newLocale).map(o => o.value).pop()
    }
  }

  onClickLogout () {
    clearCache()
    this.userService.clearUser()
    this.authService.logoutRedirect()
  }

  /**
   * Drop auth response parameters from the address bar once MSAL has consumed them.
   *
   * MSAL only clears the hash, so the `?state=...` Entra appends when redirecting back from a
   * logout lingers. Signing in from that url makes it the url MSAL returns to after the code
   * exchange, where the stale state is re-read as a response and fails with `state_mismatch`.
   */
  private clearMsalResponseFromUrl () {
    const cleaned = stripMsalResponseParams(window.location.href)
    if (cleaned === window.location.href) return

    // go through the router rather than history.replaceState: this runs during bootstrap, and the
    // router's initial navigation still holds the original url and would put the params straight back
    const { pathname, search, hash } = new URL(cleaned)
    void this.router.navigateByUrl(`${pathname}${search}${hash}`, { replaceUrl: true })
  }

  ngOnDestroy () {
    this.userSubscription?.unsubscribe()
    this.confirmationsSubscription?.unsubscribe()
  }

  // successLoginLogout (result: AuthenticationResult) {
  //   // We arrive here when the app starts.
  //   if (result) {
  //     this.authService.instance.setActiveAccount(result.account)
  //     const username = this.authService.instance.getActiveAccount()!.localAccountId
  //     this.usersApi.retrieve(username).subscribe((res: User) => {
  //       this.userService.setUser(res)
  //     })
  //   } else {
  //     // The AuthenticationResult is null (authentication failed) and there's no accounts logged in.
  //     // → delete 'Acted' user object.
  //     console.warn('Got no MSAL accounts, will call userService.clearUser()')
  //     this.userService.clearUser()
  //   }
  // }

  private displayConfirmation (toast: Toast, message: ToastMessageOptions, _: MouseEvent) {
    // Close toast item.
    toast.messages?.splice(toast.messages?.indexOf(message), 1)
    // Open Confirmation.
    this.dialogService.open(ConfirmationModalComponent, {
      header: $localize`:@@confirmations-confirmation:Confirmation`,
      width: '40rem',
      data: {
        code: message.data.confirmation.code,
      },
    })
  }
}
